/**
 * review.ts
 *
 * Pure predicates for the Title Search (TR) document review / finalize gate.
 *
 * A reviewer works through the recorded documents found for a property,
 * assigning one of three actions — accept, edit, or reject — to every extracted
 * field. A document becomes "reviewed" only once every one of its fields has an
 * action, and the finalize control unlocks only once every document is
 * reviewed. These two pure predicates encode exactly that gate so the React
 * review UI can stay a thin shell over deterministic, testable logic.
 *
 * Requirements:
 *   5.6 - a Recorded_Document is "reviewed" only when every extracted field in
 *         it has been assigned an action (accept, edit, or reject).
 *   5.7 - finalize is enabled only when every Recorded_Document is reviewed.
 *   8.5 - "Accept all >= 80%" accepts only unactioned fields at or above the
 *         confidence threshold (ui-redesign spec).
 */

import { CONFIDENCE_THRESHOLD } from '../components/ui/ConfidencePill';

/**
 * The three actions a reviewer may assign to an extracted field value
 * (Requirements 5.3, 5.4). The backend persists these as upper-case
 * (`ACCEPT` / `EDIT` / `REJECT`); the predicates below normalize casing so
 * either form is accepted.
 */
export type FieldAction = 'accept' | 'edit' | 'reject';

/** The canonical set of valid field actions, lower-cased for comparison. */
const VALID_ACTIONS: ReadonlySet<string> = new Set<FieldAction>([
  'accept',
  'edit',
  'reject',
]);

/**
 * Minimal shape of an extracted field for review purposes: it carries an
 * optional `action`. The predicates only inspect the action, so callers may
 * pass their richer field objects (with value, confidence, page, etc.) directly.
 */
export interface ReviewableField {
  action?: FieldAction | string | null;
}

/**
 * Minimal shape of a processed document for review purposes: a list of its
 * extracted fields.
 */
export interface ReviewableDocument {
  fields: ReadonlyArray<ReviewableField>;
}

/**
 * True when a single field has been assigned a valid action (accept, edit, or
 * reject). Casing and surrounding whitespace are tolerated; a missing, blank,
 * or unrecognized action counts as "no action".
 */
export function hasFieldAction(field: ReviewableField | null | undefined): boolean {
  const action = field?.action;
  if (typeof action !== 'string') {
    return false;
  }
  return VALID_ACTIONS.has(action.trim().toLowerCase());
}

/**
 * A document is "reviewed" if and only if every extracted field in it has been
 * assigned an action (accept, edit, or reject).
 *
 * A document with no extracted fields is vacuously reviewed (there is no field
 * left without an action).
 *
 * Requirements: 5.6
 */
export function isDocumentReviewed(document: ReviewableDocument | null | undefined): boolean {
  const fields = document?.fields;
  if (!Array.isArray(fields)) {
    return false;
  }
  return fields.every(hasFieldAction);
}

/**
 * The finalize action is enabled if and only if every document in the set has
 * been reviewed (see {@link isDocumentReviewed}).
 *
 * An empty set of documents is vacuously "all reviewed"; callers that want to
 * keep finalize disabled until at least one document exists should guard for an
 * empty list separately.
 *
 * Requirements: 5.7
 */
export function isFinalizeEnabled(
  documents: ReadonlyArray<ReviewableDocument> | null | undefined
): boolean {
  if (!Array.isArray(documents)) {
    return false;
  }
  return documents.every(isDocumentReviewed);
}

/** Minimal field shape for bulk accept: a stable id and a 0-1 confidence. */
export interface BulkAcceptableField {
  id: string;
  confidence: number;
}

/** Reviewer actions keyed by field id. A missing key means "no action yet". */
export type FieldActionMap = Readonly<Record<string, FieldAction | string | null | undefined>>;

/**
 * "Accept all >= 80%": returns a new actions map in which every field whose
 * confidence is at or above `threshold` and that has no action yet is set to
 * `accept`. Fields that already have an action (accept, edit, or reject) and
 * fields below the threshold are left exactly as they were. Inputs are never
 * mutated.
 *
 * Requirements: 8.5
 */
export function acceptAllAboveThreshold(
  fields: ReadonlyArray<BulkAcceptableField>,
  actions: FieldActionMap,
  threshold: number = CONFIDENCE_THRESHOLD
): Record<string, FieldAction | string | null | undefined> {
  const next: Record<string, FieldAction | string | null | undefined> = { ...actions };
  for (const field of fields) {
    const hasAction = hasFieldAction({ action: actions[field.id] });
    if (!hasAction && field.confidence >= threshold) {
      next[field.id] = 'accept';
    }
  }
  return next;
}
